/**
 * What the device does with what it heard, before (or instead of) asking the agent. The fast
 * path answers "next / skip / one / two / three / repeat / stop / approve" and "what needs me"
 * from structured data alone: options are read verbatim from the decision record, and "two" is
 * the stored option at position 2, never the agent's wording.
 */
import {
  answerFromBrief,
  type CallOption,
  type CallQueueItem,
  parseCallFastPath,
  spokenDecision,
  statusQuestion,
} from '@viko/protocol'
import { type CallState, currentItem, effectiveQueue } from './state.js'

export type DevicePlan =
  | { kind: 'pick'; item: CallQueueItem; option: CallOption; note?: string }
  | { kind: 'skip'; item: CallQueueItem }
  | { kind: 'repeat' }
  | { kind: 'stop' }
  | { kind: 'approve' }
  | { kind: 'status'; answer: string }
  /** Not the device's to answer: the agent hears it. */
  | { kind: 'agent' }

export function planFor(
  s: Pick<CallState, 'queue' | 'local' | 'brief' | 'secure'>,
  text: string,
): DevicePlan {
  const item = currentItem(s)
  const options = item?.decision?.options.length ?? 0
  const fp = parseCallFastPath(text, options)
  if (fp) {
    switch (fp.kind) {
      case 'pick': {
        const option = item?.decision?.options.find((o) => o.n === fp.n)
        // A decision that needs the passkey goes through the agent to the secure batch.
        if (!item || !option || item.decision?.secure) return { kind: 'agent' }
        return { kind: 'pick', item, option, ...(fp.note ? { note: fp.note } : {}) }
      }
      case 'next':
      case 'skip':
        return item ? { kind: 'skip', item } : { kind: 'agent' }
      case 'repeat':
        return { kind: 'repeat' }
      case 'stop':
        return { kind: 'stop' }
      case 'approve':
        return s.secure.some((x) => x.state === 'queued') ? { kind: 'approve' } : { kind: 'agent' }
    }
  }
  const q = statusQuestion(text)
  if (q && s.brief) return { kind: 'status', answer: answerFromBrief(s.brief, q.q, q.project) }
  return { kind: 'agent' }
}

/** The next item read aloud, verbatim, after a pick or skip (`exclude` just left the queue). */
export function nextItemSpeech(
  s: Pick<CallState, 'queue' | 'local'>,
  exclude: string,
): { item?: CallQueueItem; text: string } {
  const next = effectiveQueue({ ...s, local: { ...s.local, [exclude]: 'skipped' } }).find(
    (q) => q.state === 'current',
  )
  if (!next) return { text: 'That clears your queue.' }
  if (next.decision)
    return { item: next, text: `Next, ${spokenDecision(next.projectName, next.decision)}` }
  return { item: next, text: `Next, ${next.projectName}: ${next.title}.` }
}

/** "Picked 2: Drop it and tell me." (the stored label, verbatim). */
export function pickedText(o: CallOption, note?: string): string {
  return `Picked ${o.n}: ${o.label}${/[.!?]$/.test(o.label) ? '' : '.'}${note ? ` Note: ${note}` : ''}`
}
